// src/pages/Admin.jsx
// Page Administration — 2 onglets :
//   1. Gestion des utilisateurs (Admin & Super Admin)
//   2. Mon profil (tous les utilisateurs)

import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { buAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { initials } from '../utils/helpers';
import {
  Plus, Edit2, Trash2, X, Eye, EyeOff,
  Users, Shield, Briefcase, UserCheck, User,
  Lock, Mail, Phone, MapPin, ToggleLeft, ToggleRight,
  Send, Crown, HeartHandshake, ClipboardCheck, Building2, ChevronDown, ChevronUp, Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import './Admin.css';
import { extractError } from '../context/AuthContext';
// Fonction locale pour extraire le message d'erreur


// ─────────────────────────────────────────────────────────────
// Config rôles
// ─────────────────────────────────────────────────────────────
const ROLE_CONFIG = {
  SUPER_ADMIN:        { label: 'Super Admin',          icon: Crown,           color: '#7C3AED', bg: 'rgba(124,58,237,0.12)', border: 'rgba(124,58,237,0.25)' },
  ADMIN:              { label: 'Business Manager',       icon: Shield,          color: '#DC2626', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.22)' },
  BUSINESS_MANAGER:   { label: "Ingénieur d'Affaires", icon: Briefcase,       color: '#FF4100', bg: 'var(--orange-50)', border: 'rgba(255,65,0,0.22)' },
  REFERENT_TECHNIQUE: { label: 'Référent Technique',   icon: ClipboardCheck,  color: '#0891B2', bg: 'rgba(8,145,178,0.12)', border: 'rgba(8,145,178,0.25)' },
  TALENT_ACQUISITION: { label: 'Talent Acquisition',   icon: UserCheck,       color: '#EA580C', bg: 'rgba(234,88,12,0.12)', border: 'rgba(234,88,12,0.25)' },
  RH:                 { label: 'Ressources Humaines',  icon: HeartHandshake,  color: '#059669', bg: 'rgba(5,150,105,0.12)', border: 'rgba(5,150,105,0.25)' },
};

const SITES = ['FRANCE', 'BELGIQUE', 'ALGERIE', 'TUNISIE'];

// API helpers
const adminAPI = {
  listUsers:     ()           => api.get('/admin/users'),
  createUser:    (data)       => api.post('/admin/users', data),
  updateUser:    (id, data)   => api.put(`/admin/users/${id}`, data),
  deleteUser:    (id)         => api.delete(`/admin/users/${id}`),
  toggleActive:  (id)         => api.post(`/admin/users/${id}/toggle-active`),
  resendInvite:  (id)         => api.post(`/admin/users/${id}/resend-invite`),
  getMe:         ()           => api.get('/admin/me'),
  updateMe:      (data)       => api.put('/admin/me', data),
  changePassword:(data)       => api.post('/admin/me/change-password', data),
};

// ─────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────
export default function Admin() {
 /* const { user: me } = useAuth();
  const [tab, setTab] = useState('users'); // 'users' | 'bu' | 'profile'

  const role = me?.role || '';
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdmin      = role === 'ADMIN' || isSuperAdmin;
*/


 const { user: me } = useAuth();
  const [tab, setTab] = useState('users');

  const role = me?.role || '';
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdmin      = role === 'ADMIN' || isSuperAdmin;
  const isBM         = role === 'BUSINESS_MANAGER';
  const canViewRoles = isAdmin || isBM; // SUPER_ADMIN, ADMIN, BUSINESS_MANAGER


  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Administration</h1>
          <p>Gestion des utilisateurs et de votre compte</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, borderBottom:'2px solid var(--border)',
        marginBottom:24, background:'var(--bg-card)', borderRadius:'8px 8px 0 0',
        border:'1px solid var(--border)' }}>
        {[
          { key:'users', label:'Gestion des utilisateurs', show: isAdmin },
          { key:'roles', label:'Rôles',                    show: canViewRoles },
          { key:'bu',    label:'Business Units',           show: isSuperAdmin },
        ].filter(t => t.show).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding:'12px 24px', border:'none', cursor:'pointer',
            background: tab === t.key ? 'var(--bg-elevated)' : 'var(--bg-card)',
            borderBottom: tab === t.key ? '3px solid #FF4100' : '3px solid transparent',
            color: tab === t.key ? '#FF4100' : 'var(--text-muted)',
            fontWeight: tab === t.key ? 700 : 500, fontSize:'0.9rem',
            transition:'all 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

   {tab === 'users' && isAdmin      && <UsersTab me={me} isSuperAdmin={isSuperAdmin}/>}
      {tab === 'roles' && canViewRoles && <RolesTab me={me} viewerRole={role} isSuperAdmin={isSuperAdmin}/>}
      {tab === 'bu'    && isSuperAdmin && <BUTab/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Onglet Rôles — cards par rôle, ajout/retrait
// ─────────────────────────────────────────────────────────────
function RolesTab({ me, viewerRole, isSuperAdmin }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [addSelect,   setAddSelect]   = useState({}); // { roleKey: userId }
  const [reassignFor, setReassignFor] = useState(null); // user en cours de changement
  const [savingId,    setSavingId]    = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listUsers();
      setUsers(res.data || []);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const isBM = viewerRole === 'BUSINESS_MANAGER';

  // Rôles visibles selon le rôle du visualisateur
  // - SUPER_ADMIN : tout le monde
  // - ADMIN       : tout sauf SUPER_ADMIN
  // - BUSINESS_MANAGER : tout sauf SUPER_ADMIN et BUSINESS_MANAGER (son propre rôle)
  const visibleRoleKeys = Object.keys(ROLE_CONFIG).filter(key => {
    if (key === 'SUPER_ADMIN') return isSuperAdmin;
    if (key === 'BUSINESS_MANAGER') return !isBM;
    return true;
  });

  // Un rôle est "gérable" (ajout/retrait de personnes) si le visualisateur a le droit de l'assigner
  const canManageRole = (roleKey) => {
    if (roleKey === 'SUPER_ADMIN') return false;       // jamais assignable via l'UI
    if (roleKey === 'ADMIN') return isSuperAdmin;       // seul le Super Admin gère les comptes Admin
    return visibleRoleKeys.includes(roleKey);
  };

  const assignRole = async (user, newRole) => {
    setSavingId(user.id);
    try {
      await adminAPI.updateUser(user.id, { role: newRole });
      toast.success(`${user.prenom} ${user.nom} → ${ROLE_CONFIG[newRole].label}`);
      setReassignFor(null);
      setAddSelect(p => ({ ...p, [newRole]: '' }));
      fetchUsers();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingId(null); }
  };

  const handleAdd = (roleKey) => {
    const userId = addSelect[roleKey];
    if (!userId) { toast.error('Sélectionnez un utilisateur'); return; }
    const user = users.find(u => String(u.id) === String(userId));
    if (user) assignRole(user, roleKey);
  };

  if (loading) {
    return <div style={{ textAlign:'center', padding:40 }}><div className="spinner"/></div>;
  }

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:16 }}>
      {visibleRoleKeys.map(roleKey => {
        const cfg = ROLE_CONFIG[roleKey];
        const Icon = cfg.icon;
        const members = users.filter(u => u.role === roleKey);
        const manageable = canManageRole(roleKey);
        // Utilisateurs pouvant être ajoutés à ce rôle
        const available = users.filter(u =>
          u.role !== roleKey && (roleKey !== 'ADMIN' || isSuperAdmin)
        );

        return (
          <div key={roleKey} className="card" style={{ padding:0, overflow:'hidden', border:`1px solid ${cfg.border}` }}>
            {/* En-tête */}
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 16px',
              background:cfg.bg, borderBottom:`1px solid ${cfg.border}` }}>
              <Icon size={17} color={cfg.color}/>
              <span style={{ fontWeight:700, fontSize:'0.875rem', color:cfg.color, flex:1 }}>{cfg.label}</span>
              <span style={{ padding:'2px 9px', borderRadius:20, background:`${cfg.color}22`,
                color:cfg.color, fontSize:'0.75rem', fontWeight:700 }}>
                {members.length}
              </span>
            </div>

            {/* Membres */}
            <div style={{ padding:'12px 14px', display:'flex', flexDirection:'column', gap:8, minHeight:60 }}>
              {members.length === 0 ? (
                <div style={{ fontSize:'0.8125rem', color:'var(--text-muted)', fontStyle:'italic',
                  textAlign:'center', padding:'8px 0' }}>
                  Aucune personne
                </div>
              ) : members.map(u => (
                <div key={u.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 9px',
                  background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)' }}>
                  <div className="avatar" style={{ width:28, height:28, fontSize:'0.65rem', flexShrink:0 }}>
                    {initials(`${u.prenom} ${u.nom}`)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:'0.8125rem', overflow:'hidden',
                      textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {u.prenom} {u.nom}
                    </div>
                    <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', overflow:'hidden',
                      textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {u.email}
                    </div>
                  </div>
                  {manageable && (
                    <button className="btn btn-ghost btn-icon btn-sm" title="Changer de rôle"
                      onClick={() => setReassignFor(u)}
                      disabled={savingId === u.id}
                      style={{ color:'var(--danger-600)', flexShrink:0 }}>
                      <X size={13}/>
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Ajouter quelqu'un à ce rôle */}
            {manageable && available.length > 0 && (
              <div style={{ display:'flex', gap:6, padding:'10px 14px 14px', borderTop:'1px solid var(--border)' }}>
                <select
                  value={addSelect[roleKey] || ''}
                  onChange={e => setAddSelect(p => ({ ...p, [roleKey]: e.target.value }))}
                  style={{ flex:1, padding:'7px 10px', border:`1.5px solid ${cfg.border}`, borderRadius:7,
                    fontSize:'0.8125rem', background:'var(--bg-card)', color:'var(--text-primary)', outline:'none' }}
                >
                  <option value="">— Ajouter à ce rôle…</option>
                  {available.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.prenom} {u.nom} ({ROLE_CONFIG[u.role]?.label || u.role})
                    </option>
                  ))}
                </select>
                <button className="btn btn-sm" disabled={!addSelect[roleKey] || !!savingId}
                  onClick={() => handleAdd(roleKey)}
                  style={{ background:cfg.color, color:'white', border:'none', flexShrink:0,
                    opacity: addSelect[roleKey] ? 1 : 0.4 }}>
                  <Plus size={13}/>
                </button>
              </div>
            )}
          </div>
        );
      })}

      {reassignFor && (
        <ReassignRoleModal
          user={reassignFor}
          currentRole={reassignFor.role}
          isSuperAdmin={isSuperAdmin}
          saving={savingId === reassignFor.id}
          onCancel={() => setReassignFor(null)}
          onSelect={(newRole) => assignRole(reassignFor, newRole)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Modal de changement de rôle rapide
// ─────────────────────────────────────────────────────────────
function ReassignRoleModal({ user, currentRole, isSuperAdmin, saving, onCancel, onSelect }) {
  const options = Object.entries(ROLE_CONFIG).filter(([key]) => {
    if (key === currentRole) return false;
    if (key === 'SUPER_ADMIN') return false;
    if (key === 'ADMIN') return isSuperAdmin;
    return true;
  });

  return (
    <div style={{ position:'fixed', inset:0, zIndex:2500, background:'rgba(0,0,0,0.55)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
      onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ background:'var(--bg-card)', borderRadius:14, width:'100%', maxWidth:420,
        boxShadow:'0 24px 80px rgba(0,0,0,0.25)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
          <h3 style={{ margin:0, fontSize:'0.9375rem', fontWeight:700 }}>
            Changer le rôle — {user.prenom} {user.nom}
          </h3>
          <button className="btn btn-ghost btn-icon" onClick={onCancel}><X size={16}/></button>
        </div>
        <div style={{ padding:20, display:'flex', flexWrap:'wrap', gap:8 }}>
          {options.map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button key={key} type="button" disabled={saving}
                onClick={() => onSelect(key)}
                style={{ display:'flex', alignItems:'center', gap:6,
                  padding:'8px 14px', borderRadius:8, cursor: saving ? 'default' : 'pointer',
                  border:`2px solid ${cfg.border}`, background:cfg.bg, color:cfg.color,
                  fontWeight:600, fontSize:'0.8125rem' }}>
                <Icon size={14}/> {cfg.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}




// ─────────────────────────────────────────────────────────────
// Onglet Gestion utilisateurs
// ─────────────────────────────────────────────────────────────
function UsersTab({ me, isSuperAdmin }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // user à supprimer

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listUsers();
      setUsers(res.data || []);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Stats par rôle
  const stats = Object.entries(ROLE_CONFIG).map(([key, cfg]) => ({
    role: key, ...cfg,
    count: users.filter(u => u.role === key).length,
  })).filter(s => isSuperAdmin || s.role !== 'SUPER_ADMIN');

  const handleDelete = async (u) => {
    setDeleteTarget(u); // ouvre le modal de confirmation
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminAPI.deleteUser(deleteTarget.id);
      toast.success(`Compte de ${deleteTarget.prenom} ${deleteTarget.nom} supprimé`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleToggle = async (u) => {
    const action = u.is_active ? 'désactiver' : 'activer';
    try {
      await adminAPI.toggleActive(u.id);
      toast.success(`Compte ${u.is_active ? 'désactivé' : 'activé'}`);
      fetchUsers();
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleResend = async (u) => {
    try {
      await adminAPI.resendInvite(u.id);
      toast.success('Invitation renvoyée par email');
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <>
      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:12, marginBottom:24 }}>
        {stats.map(({ role, label, icon:Icon, color, bg, border, count }) => (
          <div key={role} style={{ background:bg, border:`1px solid ${border}`, borderRadius:10, padding:'14px 16px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <Icon size={16} color={color}/>
              <span style={{ fontSize:'0.75rem', fontWeight:700, color, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</span>
            </div>
            <div style={{ fontSize:'1.75rem', fontWeight:800, color }}>{count}</div>
          </div>
        ))}
      </div>

      {/* Tableau */}
      <div className="card">
        <div className="card-header">
          <span className="card-title"><Users size={15} style={{ verticalAlign:'middle', marginRight:6 }}/> Utilisateurs ({users.length})</span>
          <button className="btn btn-orange" onClick={() => { setEditing(null); setModal(true); }}>
            <Plus size={15}/> Nouvel utilisateur
          </button>
        </div>

        {loading ? (
          <div style={{ padding:32, display:'flex', alignItems:'center', gap:10, color:'var(--text-muted)' }}>
            <div className="spinner"/> Chargement…
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Email</th>
                  <th>Rôle</th>
                  <th>Site</th>
                  <th>Statut</th>
                  <th style={{ textAlign:'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const cfg = ROLE_CONFIG[u.role] || ROLE_CONFIG.TALENT_ACQUISITION;
                  const Icon = cfg.icon;
                  const isMe = u.id === me?.id;
                  return (
                    <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.55 }}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div className="avatar avatar-md" style={{ border: isMe ? '2px solid #FF4100' : undefined }}>
                            {initials(`${u.prenom} ${u.nom}`)}
                          </div>
                          <div>
                            <div style={{ fontWeight:600, fontSize:'0.875rem' }}>
                              {u.prenom} {u.nom} {isMe && <span style={{ fontSize:'0.6875rem', color:'#FF4100', fontWeight:700 }}>(moi)</span>}
                            </div>
                            {u.telephone && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{u.telephone}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--text-secondary)' }}>{u.email}</td>
                      <td>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:20,
                          background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}`,
                          fontSize:'0.75rem', fontWeight:700 }}>
                          <Icon size={11}/> {cfg.label}
                        </span>
                      </td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{u.site || '—'}</td>
                      <td>
                        <span style={{ padding:'3px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:700,
                          background: u.is_active ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                          color: u.is_active ? '#4ADE80' : '#dc2626' }}>
                          {u.is_active ? '● Actif' : '○ Inactif'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:4, justifyContent:'flex-end' }}>
                          {/* Modifier */}
                          <button className="btn btn-ghost btn-icon btn-sm" title="Modifier"
                            onClick={() => { setEditing(u); setModal(true); }}>
                            <Edit2 size={13}/>
                          </button>
                          {/* Renvoyer invitation */}
                          <button className="btn btn-ghost btn-icon btn-sm" title="Renvoyer l'invitation"
                            onClick={() => handleResend(u)}>
                            <Send size={13}/>
                          </button>
                          {/* Activer/Désactiver — Super Admin only */}
                          {isSuperAdmin && !isMe && (
                            <button className="btn btn-ghost btn-icon btn-sm"
                              title={u.is_active ? 'Désactiver' : 'Activer'}
                              onClick={() => handleToggle(u)}
                              style={{ color: u.is_active ? '#f97316' : '#22c55e' }}>
                              {u.is_active ? <ToggleRight size={16}/> : <ToggleLeft size={16}/>}
                            </button>
                          )}
                          {/* Supprimer — Super Admin only */}
                          {isSuperAdmin && !isMe && (
                            <button className="btn btn-danger btn-icon btn-sm" title="Supprimer"
                              onClick={() => handleDelete(u)}>
                              <Trash2 size={13}/>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr><td colSpan={6}>
                    <div style={{ padding:32, textAlign:'center', color:'var(--text-muted)' }}>
                      <Users size={28}/><br/>Aucun utilisateur
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal création / modification */}
      {modal && (
        <UserModal
          user={editing}
          me={me}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); fetchUsers(); }}
        />
      )}

      {/* Modal confirmation suppression */}
      {deleteTarget && (
        <DeleteConfirmModal
          user={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Modal de confirmation suppression — sécurisé et explicite
// ─────────────────────────────────────────────────────────────
function DeleteConfirmModal({ user, onCancel, onConfirm }) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const expected = user.email;
  const isValid = typed.trim() === expected;

  const handleConfirm = async () => {
    if (!isValid) return;
    setConfirming(true);
    await onConfirm();
    setConfirming(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:3000, background:'rgba(0,0,0,0.65)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
      onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ background:'var(--bg-card)', borderRadius:14, width:'100%', maxWidth:480,
        boxShadow:'0 24px 80px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'20px 24px',
          borderBottom:'1px solid rgba(239,68,68,0.20)', background:'rgba(239,68,68,0.10)', borderRadius:'14px 14px 0 0' }}>
          <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(239,68,68,0.14)',
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Trash2 size={18} color="#dc2626"/>
          </div>
          <div>
            <div style={{ fontWeight:700, color:'#0f172a', fontSize:'1rem' }}>Supprimer le compte</div>
            <div style={{ fontSize:'0.8125rem', color:'#dc2626', fontWeight:600 }}>
              Cette action est irréversible
            </div>
          </div>
        </div>

        {/* Corps */}
        <div style={{ padding:'20px 24px' }}>
          <div style={{ fontSize:'0.9rem', color:'var(--text-primary)', lineHeight:1.7, marginBottom:16 }}>
            Vous êtes sur le point de supprimer définitivement le compte de{' '}
            <strong>{user.prenom} {user.nom}</strong>.<br/>
            Ses AO assignés seront désassignés, ses notifications supprimées.
          </div>

          <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8,
            padding:'12px 14px', marginBottom:16 }}>
            <div style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)',
              textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>
              Pour confirmer, saisissez l'adresse email du compte :
            </div>
            <div style={{ fontSize:'0.875rem', fontWeight:700, color:'#dc2626', marginBottom:8,
              padding:'6px 10px', background:'rgba(239,68,68,0.10)', borderRadius:6, fontFamily:'monospace' }}>
              {expected}
            </div>
            <input
              style={{ width:'100%', padding:'9px 12px', border:`2px solid ${isValid ? '#22c55e' : 'var(--border)'}`,
                borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box',
                fontFamily:'monospace', outline:'none', transition:'border-color 0.15s' }}
              placeholder="Saisissez l'email ici…"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              autoFocus
            />
            {isValid && (
              <div style={{ fontSize:'0.75rem', color:'#22c55e', fontWeight:600, marginTop:6 }}>
                Email confirmé
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display:'flex', justifyContent:'flex-end', gap:8,
          padding:'14px 24px', borderTop:'1px solid var(--border)', background:'var(--bg-elevated)',
          borderRadius:'0 0 14px 14px' }}>
          <button className="btn btn-secondary" onClick={onCancel}>Annuler</button>
          <button
            onClick={handleConfirm}
            disabled={!isValid || confirming}
            style={{ display:'inline-flex', alignItems:'center', gap:6,
              padding:'9px 18px', border:'none', borderRadius:8, cursor: isValid ? 'pointer' : 'not-allowed',
              background: isValid ? '#dc2626' : 'var(--bg-elevated)', color: isValid ? 'white' : 'var(--text-muted)',
              fontWeight:700, fontSize:'0.875rem', transition:'all 0.15s' }}>
            {confirming
              ? <><div className="spinner" style={{ width:13, height:13, borderTopColor:'white' }}/> Suppression…</>
              : <><Trash2 size={14}/> Supprimer définitivement</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Modal création / modification utilisateur
// ─────────────────────────────────────────────────────────────
function UserModal({ user, me, isSuperAdmin, onClose, onSaved }) {
  const isEdit = !!user;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    prenom:      user?.prenom      || '',
    nom:         user?.nom         || '',
    email:       user?.email       || '',
    telephone:   user?.telephone   || '',
    site:        user?.site        || '',
    role:        user?.role        || 'TALENT_ACQUISITION',
    is_active:   user?.is_active   ?? true,
    send_invite: true,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Rôles disponibles selon le rôle du créateur
  const availableRoles = Object.entries(ROLE_CONFIG).filter(([key]) => {
    if (key === 'SUPER_ADMIN') return false; // jamais créable via UI
    if (key === 'ADMIN')       return isSuperAdmin;
    return true;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {};
        if (form.nom)       payload.nom       = form.nom;
        if (form.prenom)    payload.prenom    = form.prenom;
        if (form.telephone) payload.telephone = form.telephone;
        if (form.site)      payload.site      = form.site;
        payload.role      = form.role;
        payload.is_active = form.is_active;
        await adminAPI.updateUser(user.id, payload);
        toast.success('Utilisateur mis à jour');
      } else {
        await adminAPI.createUser({
          email:       form.email,
          nom:         form.nom,
          prenom:      form.prenom,
          telephone:   form.telephone,
          site:        form.site,
          role:        form.role,
          send_invite: form.send_invite,
          // Pas de password → le backend génère un token et envoie l'email
        });
        toast.success(form.send_invite
          ? `✅ Compte créé ! Un email d'invitation a été envoyé à ${form.email}`
          : '✅ Compte créé');
      }
      onSaved();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:2000, background:'rgba(0,0,0,0.55)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--bg-card)', borderRadius:14, width:'100%', maxWidth:580,
        maxHeight:'90vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 80px rgba(0,0,0,0.25)' }}>

        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'18px 24px', borderBottom:'1px solid var(--border)' }}>
          <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700 }}>
            {isEdit ? `✏️ Modifier — ${user.prenom} ${user.nom}` : '👤 Nouvel utilisateur'}
          </h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18}/></button>
        </div>

        <div style={{ flex:1, overflow:'auto', padding:24 }}>
          {/* Sélecteur de rôle */}
          <div style={{ marginBottom:20 }}>
            <label style={lbl}>Rôle *</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {availableRoles.map(([key, cfg]) => {
                const Icon = cfg.icon;
                const sel  = form.role === key;
                return (
                  <button key={key} type="button"
                    onClick={() => set('role', key)}
                    style={{ display:'flex', alignItems:'center', gap:6,
                      padding:'8px 14px', borderRadius:8, cursor:'pointer',
                      border: sel ? `2px solid ${cfg.color}` : '2px solid var(--border)',
                      background: sel ? cfg.bg : 'var(--bg-card)',
                      color: sel ? cfg.color : 'var(--text-muted)',
                      fontWeight: sel ? 700 : 500, fontSize:'0.8125rem',
                      transition:'all 0.15s' }}>
                    <Icon size={14}/> {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' }}>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Prénom *</label>
              <input style={inp} value={form.prenom} onChange={e => set('prenom', e.target.value)} placeholder="Jean"/>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Nom *</label>
              <input style={inp} value={form.nom} onChange={e => set('nom', e.target.value)} placeholder="Dupont"/>
            </div>
            <div style={{ marginBottom:14, gridColumn:'1/-1' }}>
              <label style={lbl}>Email * {isEdit && <span style={{ fontWeight:400, color:'var(--text-muted)' }}>(non modifiable)</span>}</label>
              <input style={{ ...inp, background: undefined }}
                type="email" value={form.email}
                onChange={e => !isEdit && set('email', e.target.value)}
                readOnly={isEdit} placeholder="jean.dupont@wikeys.fr"/>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Téléphone</label>
              <input style={inp} type="tel" value={form.telephone} onChange={e => set('telephone', e.target.value)} placeholder="+33 6 00 00 00 00"/>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Site</label>
              <select style={inp} value={form.site} onChange={e => set('site', e.target.value)}>
                <option value="">— Sélectionner —</option>
                {SITES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Statut (modification seulement, Super Admin uniquement) */}
          {isEdit && isSuperAdmin && user.id !== me?.id && (
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
              background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)', marginTop:8 }}>
              <label style={{ fontWeight:600, fontSize:'0.875rem', flex:1 }}>Compte actif</label>
              <button type="button" onClick={() => set('is_active', !form.is_active)}
                style={{ background:'none', border:'none', cursor:'pointer', padding:0 }}>
                {form.is_active
                  ? <ToggleRight size={28} color="#22c55e"/>
                  : <ToggleLeft  size={28} color="#9CA3AF"/>}
              </button>
            </div>
          )}

          {/* Option invitation email (création uniquement) */}
          {!isEdit && (
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
              background:'var(--orange-50)', borderRadius:8, border:'1px solid rgba(255,65,0,0.22)', marginTop:12 }}>
              <Mail size={16} color="#FF4100"/>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600, fontSize:'0.875rem', color:'#1e40af' }}>Envoyer l'invitation par email</div>
                <div style={{ fontSize:'0.75rem', color:'#3B82F6' }}>
                  L'utilisateur recevra un lien pour créer son mot de passe
                </div>
              </div>
              <input type="checkbox" checked={form.send_invite}
                onChange={e => set('send_invite', e.target.checked)}
                style={{ width:16, height:16, cursor:'pointer' }}/>
            </div>
          )}
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:8,
          padding:'14px 24px', borderTop:'1px solid var(--border)', background:'var(--bg-card)' }}>
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-orange" onClick={handleSave} disabled={saving}
            style={{ background:'#FF4100', borderColor:'#FF4100' }}>
            {saving ? <><div className="spinner" style={{ width:13, height:13 }}/> Enregistrement…</>
                    : isEdit ? '💾 Enregistrer' : '✉️ Créer & Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Onglet Mon profil
// ─────────────────────────────────────────────────────────────
function ProfileTab({ me }) {
  const [profile, setProfile]   = useState(null);
  const [saving,  setSaving]    = useState(false);
  const [pwForm,  setPwForm]    = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [showPw,  setShowPw]    = useState({ current:false, new:false, confirm:false });
  const [savingPw,setSavingPw]  = useState(false);

  useEffect(() => {
    adminAPI.getMe().then(r => setProfile(r.data)).catch(() => setProfile(me));
  }, [me]);

  const set   = (k, v) => setProfile(p => ({ ...p, [k]: v }));
  const setPw = (k, v) => setPwForm(p => ({ ...p, [k]: v }));

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.updateMe({
        nom: profile.nom, prenom: profile.prenom,
        telephone: profile.telephone, site: profile.site,
      });
      setProfile(res.data);
      toast.success('Profil mis à jour');
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas'); return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('Au moins 8 caractères'); return;
    }
    setSavingPw(true);
    try {
      await adminAPI.changePassword(pwForm);
      setPwForm({ current_password:'', new_password:'', confirm_password:'' });
      toast.success('✅ Mot de passe modifié — un email de confirmation vous a été envoyé');
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingPw(false); }
  };

  if (!profile) return <div style={{ padding:32, display:'flex', gap:10, color:'var(--text-muted)' }}><div className="spinner"/> Chargement…</div>;

  const cfg = ROLE_CONFIG[profile.role] || ROLE_CONFIG.TALENT_ACQUISITION;
  const Icon = cfg.icon;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

      {/* Infos personnelles */}
      <div className="card" style={{ gridColumn:'1/-1' }}>
        <div className="card-header">
          <span className="card-title"><User size={15} style={{ verticalAlign:'middle', marginRight:6 }}/> Informations personnelles</span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 12px', borderRadius:20,
            background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}`, fontSize:'0.75rem', fontWeight:700 }}>
            <Icon size={11}/> {cfg.label}
          </span>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px', padding:'4px 0' }}>
          {[
            { label:'Prénom', key:'prenom', placeholder:'Jean' },
            { label:'Nom',    key:'nom',    placeholder:'Dupont' },
          ].map(({ label, key, placeholder }) => (
            <div key={key} style={{ marginBottom:14 }}>
              <label style={lbl}>{label}</label>
              <input style={inp} value={profile[key]||''} onChange={e => set(key, e.target.value)} placeholder={placeholder}/>
            </div>
          ))}
          <div style={{ marginBottom:14 }}>
            <label style={lbl}><Mail size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>Email</label>
            <input style={{ ...inp, background:'var(--bg-elevated)', color:'var(--text-muted)' }}
              value={profile.email||''} readOnly/>
            <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:3 }}>L'email ne peut pas être modifié</div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lbl}><Phone size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>Téléphone</label>
            <input style={inp} type="tel" value={profile.telephone||''} onChange={e => set('telephone', e.target.value)} placeholder="+33 6 00 00 00 00"/>
          </div>
          <div style={{ marginBottom:14, gridColumn:'1/-1' }}>
            <label style={lbl}><MapPin size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>Site</label>
            <select style={inp} value={profile.site||''} onChange={e => set('site', e.target.value)}>
              <option value="">— Sélectionner —</option>
              {SITES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginTop:4 }}>
          <button className="btn btn-orange" onClick={handleSaveProfile} disabled={saving}
            style={{ background:'#FF4100', borderColor:'#FF4100' }}>
            {saving ? <><div className="spinner" style={{ width:13, height:13 }}/> Enregistrement…</> : '💾 Enregistrer les modifications'}
          </button>
        </div>
      </div>

      {/* Changer mot de passe */}
      <div className="card" style={{ gridColumn:'1/-1' }}>
        <div className="card-header">
          <span className="card-title"><Lock size={15} style={{ verticalAlign:'middle', marginRight:6 }}/> Changer mon mot de passe</span>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0 16px' }}>
          {[
            { label:'Mot de passe actuel', key:'current_password' },
            { label:'Nouveau mot de passe', key:'new_password' },
            { label:'Confirmer',            key:'confirm_password' },
          ].map(({ label, key }) => (
            <div key={key} style={{ marginBottom:14 }}>
              <label style={lbl}>{label}</label>
              <div style={{ position:'relative' }}>
                <input style={{ ...inp, paddingRight:38 }}
                  type={showPw[key.split('_')[0]] ? 'text' : 'password'}
                  value={pwForm[key]}
                  onChange={e => setPw(key, e.target.value)}
                  placeholder="••••••••"/>
                <button type="button" onClick={() => setShowPw(s => ({ ...s, [key.split('_')[0]]: !s[key.split('_')[0]] }))}
                  style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)' }}>
                  {showPw[key.split('_')[0]] ? <EyeOff size={15}/> : <Eye size={15}/>}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:14 }}>
          Un email de confirmation vous sera envoyé après le changement.
        </div>
        <button className="btn btn-secondary" onClick={handleChangePassword} disabled={savingPw}>
          {savingPw ? <><div className="spinner" style={{ width:13, height:13 }}/> Modification…</> : '🔐 Changer le mot de passe'}
        </button>
      </div>
    </div>
  );
}

// Styles partagés
const lbl = {
  display:'block', fontSize:'0.8125rem', fontWeight:600,
  color:'var(--text-muted)', marginBottom:5,
  textTransform:'uppercase', letterSpacing:'0.05em',
};
const inp = {
  width:'100%', padding:'9px 12px', border:'1px solid var(--border)',
  borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box',
  fontFamily:'inherit', outline:'none',
  background:'var(--bg-elevated)', color:'var(--text-primary)',
};


// ─────────────────────────────────────────────────────────────
// Onglet Business Units (Super Admin uniquement)
// ─────────────────────────────────────────────────────────────
const BU_ROLE_LABELS = {
  ADMIN_BU:           { label: 'Admin BU',          color: '#DC2626', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.22)' },
  COMMERCIAL:         { label: 'Commercial',         color: '#FF4100', bg: 'var(--orange-50)', border: 'rgba(255,65,0,0.22)' },
  TALENT_ACQUISITION: { label: 'Talent Acquisition', color: '#EA580C', bg: 'rgba(234,88,12,0.12)', border: 'rgba(234,88,12,0.25)' },
};

// Rôles app éligibles par rôle BU
const BU_ROLE_ELIGIBLE = {
  ADMIN_BU:           ['SUPER_ADMIN', 'ADMIN', 'BUSINESS_MANAGER'],
  COMMERCIAL:         ['BUSINESS_MANAGER'],
  TALENT_ACQUISITION: ['TALENT_ACQUISITION'],
};

function BUTab() {
  const [bus,      setBus]      = useState([]);
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState({});
  const [creating, setCreating] = useState(false);
  const [editBU,   setEditBU]   = useState(null);
  const [form,     setForm]     = useState({ name:'', description:'', logo_url:'' });
  const [saving,   setSaving]   = useState(false);
  // addSelect[buId.buRole] = selected userId
  const [addSelect, setAddSelect] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [busRes, usersRes] = await Promise.all([buAPI.list(), api.get('/admin/users')]);
      setBus(busRes.data || []);
      setUsers(usersRes.data || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Nom obligatoire'); return; }
    setSaving(true);
    try {
      await buAPI.create({ name: form.name.trim(), description: form.description || null, logo_url: form.logo_url || null });
      toast.success('BU créée'); setCreating(false); setForm({ name:'', description:'', logo_url:'' }); fetchAll();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!editBU || !form.name.trim()) { toast.error('Nom obligatoire'); return; }
    setSaving(true);
    try {
      await buAPI.update(editBU.id, { name: form.name.trim(), description: form.description || null, logo_url: form.logo_url || null });
      toast.success('BU mise à jour'); setEditBU(null); setForm({ name:'', description:'', logo_url:'' }); fetchAll();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };

  const handleDelete = async (bu) => {
    if (!window.confirm(`Supprimer la BU "${bu.name}" ?`)) return;
    try { await buAPI.remove(bu.id); toast.success('BU supprimée'); fetchAll(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const handleAddMember = async (buId, buRole) => {
    const key = `${buId}.${buRole}`;
    const userId = addSelect[key];
    if (!userId) { toast.error('Sélectionnez un utilisateur'); return; }
    const user = users.find(u => u.id === userId);
    try {
      await buAPI.addMember(buId, { user_id: userId, bu_role: buRole });
      toast.success(`${user?.prenom || ''} ${user?.nom || ''} ajouté`);
      setAddSelect(p => ({ ...p, [key]: '' }));
      fetchAll();
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleRemoveMember = async (buId, userId, name) => {
    try { await buAPI.removeMember(buId, userId); toast.success(`${name} retiré`); fetchAll(); }
    catch (e) { toast.error(extractError(e)); }
  };

  if (loading) return <div style={{ textAlign:'center', padding:40 }}><div className="spinner"/></div>;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:'1.1rem', fontWeight:700, margin:0 }}>Business Units</h2>
          <p style={{ color:'var(--text-muted)', fontSize:'0.875rem', margin:0 }}>{bus.length} BU{bus.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setCreating(true); setEditBU(null); setForm({ name:'', description:'', logo_url:'' }); }}>
          <Plus size={14}/> Nouvelle BU
        </button>
      </div>

      {/* Formulaire création/édition */}
      {(creating || editBU) && (
        <div className="card" style={{ padding:20, marginBottom:20, border:'2px solid #FF410033' }}>
          <div style={{ fontWeight:700, marginBottom:14, color:'#FF4100' }}>
            {editBU ? `Modifier — ${editBU.name}` : 'Nouvelle Business Unit'}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div><label style={lbl}>Nom *</label>
              <input style={inp} value={form.name} onChange={e => setForm(p => ({...p, name:e.target.value}))} placeholder="ex: Wikeys France"/></div>
            <div><label style={lbl}>Logo URL</label>
              <input style={inp} value={form.logo_url} onChange={e => setForm(p => ({...p, logo_url:e.target.value}))} placeholder="https://..."/></div>
          </div>
          <div style={{ marginBottom:14 }}><label style={lbl}>Description</label>
            <input style={inp} value={form.description} onChange={e => setForm(p => ({...p, description:e.target.value}))} placeholder="Description de la BU…"/>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-primary btn-sm" onClick={editBU ? handleUpdate : handleCreate} disabled={saving}>
              {saving ? <div className="spinner" style={{ width:12,height:12 }}/> : <Save size={13}/>}
              {editBU ? ' Enregistrer' : ' Créer'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setCreating(false); setEditBU(null); }}>Annuler</button>
          </div>
        </div>
      )}

      {bus.length === 0 ? (
        <div className="card" style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>
          <Building2 size={40} style={{ opacity:0.2, marginBottom:12 }}/>
          <div>Aucune Business Unit — créez-en une</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {bus.map(bu => {
            const isExp = !!expanded[bu.id];
            const memberIds = new Set(bu.members.map(m => String(m.user_id)));
            const sections = [
              { buRole:'ADMIN_BU',           members: bu.members.filter(m => m.bu_role === 'ADMIN_BU') },
              { buRole:'COMMERCIAL',         members: bu.members.filter(m => m.bu_role === 'COMMERCIAL') },
              { buRole:'TALENT_ACQUISITION', members: bu.members.filter(m => m.bu_role === 'TALENT_ACQUISITION') },
            ];

            return (
              <div key={bu.id} className="card" style={{ overflow:'hidden' }}>
                {/* Header */}
                <div style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 20px', cursor:'pointer', background: isExp ? 'var(--bg-elevated)' : 'var(--bg-card)' }}
                  onClick={() => setExpanded(p => ({...p, [bu.id]: !p[bu.id]}))}>
                  <div style={{ width:40, height:40, borderRadius:8, background:'#FF410018', border:'2px solid #FF410033',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
                    {bu.logo_url
                      ? <img src={bu.logo_url} alt={bu.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      : <Building2 size={18} color="#FF4100"/>}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:'0.9375rem' }}>{bu.name}</div>
                    <div style={{ fontSize:'0.8125rem', color:'var(--text-muted)', marginTop:2 }}>
                      {bu.description || '—'}
                      <span style={{ marginLeft:10, fontWeight:600 }}>
                        {sections[0].members.length} Admin BU · {sections[1].members.length} Commercial · {sections[2].members.length} TA
                      </span>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6 }} onClick={e => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditBU(bu); setCreating(false); setForm({ name:bu.name, description:bu.description||'', logo_url:bu.logo_url||'' }); }}>
                      <Edit2 size={13}/>
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ color:'var(--danger-600)' }} onClick={() => handleDelete(bu)}>
                      <Trash2 size={13}/>
                    </button>
                  </div>
                  <div style={{ color:'var(--text-muted)', flexShrink:0 }}>
                    {isExp ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                  </div>
                </div>

                {/* Détail */}
                {isExp && (
                  <div style={{ padding:'16px 20px 20px', borderTop:'1px solid var(--border)', display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                    {sections.map(({ buRole, members }) => {
                      const cfg = BU_ROLE_LABELS[buRole];
                      const eligible = BU_ROLE_ELIGIBLE[buRole];
                      const key = `${bu.id}.${buRole}`;
                      // Utilisateurs éligibles pour ce rôle BU ET pas déjà dans cette BU
                      const available = users.filter(u =>
                        eligible.includes(u.role) && !memberIds.has(String(u.id))
                      );

                      return (
                        <div key={buRole} style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:10, padding:'14px 16px', display:'flex', flexDirection:'column', gap:10 }}>
                          {/* Titre section */}
                          <div style={{ fontWeight:700, fontSize:'0.8125rem', color:cfg.color, display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ width:8, height:8, borderRadius:'50%', background:cfg.color, flexShrink:0 }}/>
                            {cfg.label}
                            <span style={{ marginLeft:'auto', padding:'1px 7px', borderRadius:20, background:`${cfg.color}20`, fontSize:'0.7rem', fontWeight:700 }}>
                              {members.length}
                            </span>
                          </div>

                          {/* Liste membres */}
                          <div style={{ display:'flex', flexDirection:'column', gap:6, minHeight:32 }}>
                            {members.length === 0 ? (
                              <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', fontStyle:'italic' }}>Aucun membre</div>
                            ) : members.map(m => (
                              <div key={m.user_id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', background:'var(--bg-card)', borderRadius:7, border:`1px solid ${cfg.border}` }}>
                                <div className="avatar" style={{ width:26, height:26, fontSize:'0.6rem', background:`${cfg.color}18`, color:cfg.color, flexShrink:0 }}>
                                  {initials(m.full_name || '??')}
                                </div>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ fontWeight:600, fontSize:'0.8125rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.full_name}</div>
                                  <div style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>{m.email}</div>
                                </div>
                                <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--danger-600)', padding:3, flexShrink:0 }}
                                  onClick={() => handleRemoveMember(bu.id, m.user_id, m.full_name)}>
                                  <X size={12}/>
                                </button>
                              </div>
                            ))}
                          </div>

                          {/* Dropdown ajout */}
                          {available.length > 0 && (
                            <div style={{ display:'flex', gap:6, marginTop:4 }}>
                              <select
                                value={addSelect[key] || ''}
                                onChange={e => setAddSelect(p => ({...p, [key]: e.target.value}))}
                                style={{ flex:1, padding:'6px 10px', border:`1.5px solid ${cfg.border}`, borderRadius:7, fontSize:'0.8125rem', background:'var(--bg-card)', color:'var(--text-primary)', outline:'none' }}
                              >
                                <option value="">— Ajouter…</option>
                                {available.map(u => (
                                  <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>
                                ))}
                              </select>
                              <button
                                className="btn btn-sm"
                                disabled={!addSelect[key]}
                                onClick={() => handleAddMember(bu.id, buRole)}
                                style={{ background:cfg.color, color:'white', border:'none', flexShrink:0, opacity: addSelect[key] ? 1 : 0.4 }}
                              >
                                <Plus size={13}/>
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
